<template>
	<v-container
		fluid
	>
		<v-row>
			<v-col>
				<v-card>
					<v-card-title>
						<translate>Product Pricing Model Output</translate>
					</v-card-title>
					<v-card-text>
						<v-row>
							<v-col>
								<h4>
									<translate>
										Device Filter
									</translate>
								</h4>
								<device-search
									v-model="deviceId"
								/>
							</v-col>
						</v-row>
						<v-row>
							<v-col cols="4">
								<date-text-field
									v-model="date"
									:label="$gettext('Date')"
									:required-message="$gettext('Date is required')"
									variant="outlined"
								/>
							</v-col>
						</v-row>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>
		<v-row>
			<v-col>
				<v-card>
					<v-card-text>
						<v-row>
							<v-col class="d-flex justify-end">
								<v-btn
									:disabled="deviceId === null && date.length !== 10"
									:loading="loading"
									color="green"
									variant="text"
									@click="exportTable"
								>
									<translate>
										Export
									</translate>
									<v-icon icon="$cloudDownload" />
								</v-btn>
							</v-col>
						</v-row>
						<v-row>
							<v-col>
								<model-backend-table
									ref="pricingTable"
									v-model="items"
									v-model:expanded="expanded"
									v-model:options="options"
									endpoint="/bo/pricing/table/index"
									show-expand
									:headers="tableHeaders"
									item-value="uniqueId"
									:filter="filter"
									:items-per-page="10"
									:items-per-page-options="[10, 50, 100]"
								>
									<template #expanded-row="{ columns, item }">
										<td />
										<td />
										<td :colspan="columns.length">
											Claim Frequency: {{ item.k }}<br>
											Loss Claim Frequency: {{ item.lc }}<br>
											Brand Claim Frequency: {{ item.kb }}<br>
											Series Claim Frequency: {{ item.ks }}<br>
											Brand Loss Claim Frequency: {{ item.lcb }}<br>
											Max Group Price: {{ item.mg }}<br>
										</td>
									</template>
									<template #item.iso6391="{ value }">
										<v-img
											:src="`/images/flags/${value}.svg`"
											width="32px"
											height="24px"
										/>
									</template>
									<template #item.premium="{ item }">
										{{ item.premium.toFixed(2) }}
									</template>
									<template #item.damage_deductible="{ item }">
										{{ item.damageDeductible.toFixed(2) }}
									</template>
									<template #item.loss_deductible="{ item }">
										{{ item.lossDeductible.toFixed(2) }}
									</template>
									<template #item.invalidated="{ item }">
										<template v-if="item.invalidated">
											{{ item.invalidated }}
										</template>
										<confirm-button
											v-else
											v-can="'pricing.update'"
											color="error"
											variant="flat"
											:callback="() => invalidate(item.id)"
											:text="$gettext('Are you sure you want to invalidate this price?')"
										>
											<translate>
												Invalidate
											</translate>
										</confirm-button>
									</template>
								</model-backend-table>
							</v-col>
						</v-row>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import ModelBackendTable from '../../../molecules/ModelBackendTable.vue';
import DeviceSearch from '../../../molecules/DeviceSearch.vue';
import axios from 'axios';
import {
	displayError,
} from 'laravel-front-library';
import DateTextField from '../../../atoms/DateTextField.vue';
import ConfirmButton from '../../../atoms/ConfirmButton.vue';
export default {
	name: 'PricingTable',
	components: {
		ConfirmButton,
		DateTextField,
		DeviceSearch,
		ModelBackendTable
	},
	data () {
		return {
			items: [],
			deviceId: null,
			date: '',
			dateMenu: false,
			loading: false,
			options: {
			},
			expanded: [],
		};
	},

	computed: {
		/**
		 * @returns {object}
		 */
		filter: function () {
			return {
				'valid': this.date.length === 10 ? this.date : '',
				'device_id': this.deviceId
			};
		},

		tableHeaders () {
			return [
				{
					title: this.$gettext('Domain'), value: 'iso6391',
				},
				{
					title: this.$gettext('Product Type'), value: 'productType',
				},
				{
					title: this.$gettext('Device Name'), value: 'fullName',
				},
				{
					title: this.$gettext('Device ID'), value: 'deviceId',
				},
				{
					title: this.$gettext('Premium'), value: 'premium',
				},
				{
					title: this.$gettext('Damage Deductible'), value: 'damageDeductible',
				},
				{
					title: this.$gettext('Loss Deductible'), value: 'lossDeductible',
				},
				{
					title: this.$gettext('Valid'), value: 'valid',
				},
				{
					title: this.$gettext('Invalidated'), value: 'invalidated',
				},
			];
		}
	},

	methods: {
		displayError,

		/**
		 * Get current date string: dd-mm-yyyy
		 *
		 * @returns {string} date string
		 */
		now () {
			const date = new Date();

			return `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}-${('0' + date.getDate()).slice(-2)}`;
		},

		/**
		 * Export the pricing table to a csv format
		 *
		 * @returns {void}
		 */
		exportTable () {
			this.loading = true;
			axios.get('/bo/pricing/table/report', {
				params: this.filter
			}).then((response) => {
				const fileURL = window.URL.createObjectURL(new Blob([response.data, ]));
				const fileLink = document.createElement('a');

				fileLink.href = fileURL;
				fileLink.setAttribute(
					'download',
					`Pricing_${this.deviceId ? this.deviceId : 'All'}_${this.date ? this.date : `All_${this.now()}`}.csv`
				);
				document.body.appendChild(fileLink);

				fileLink.click();
			}).catch((error) => {
				this.displayError(error);
			}).finally(() => {
				this.loading = false;
			});
		},

		/**
		 * @param {number} priceId
		 */
		invalidate (priceId) {
			axios.post('/api/v0/prices/invalidate', {
				'price_id': priceId
			}).then(() => {
				this.$refs.pricingTable.index();
			}).catch((error) => {
				this.displayError(error);
			});
		}
	}
};
</script>

<style scoped>

</style>
